import { Select } from 'antd';
import moment from 'moment';
import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { Button } from '../../components/Button/Button';
import { sortByHighestSeverity } from '../../logic/store/Insights/InsightsModels';
import { useAppSelector } from '../../logic/store/Store';
import { ReportView } from './Report';
import { SingleRow } from './SingleRow';
import { FeedbackThumbs } from './Feedback';
import { ArrowBackIcon } from './Icons/ArrowBackIcon';
import { SectionHeader } from './Components/SectionHeader';

const StyledDateRowContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 12px;

	// mobile
	@media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
		.ant-select {
			width: 100% !important;
		}
	}
`;


interface ISingleSalesRepViewProps
{
	onSelectSeller: (Id: string) => void;
	changeSnapshotDate: (Id: string, snapshotDate: string) => void;
	backToLastView: () => void;
}

export const SingleSalesRepView = (props: ISingleSalesRepViewProps) =>
{
	const theme = useTheme();

	const { backToLastView, onSelectSeller, changeSnapshotDate } = props;

	const selectedSalesRepId = useAppSelector(s => s.insights.currentSalesRepId);
	const salesRepAnalysis = useAppSelector(s => s.insights.singleSalesRepAnalysis);
	const salesRepFromTerritoryAnalysis = useAppSelector(
		s => s.insights.singleTerritoryAnalysis?.SalesRepAnalyses
			?.find(salesRep => salesRep.SalesRepUserId === selectedSalesRepId)
	);
	const relationshipTitle = salesRepAnalysis?.RelationshipTitle 
		|| salesRepFromTerritoryAnalysis?.RelationshipTitle 
		|| 'Sales Rep';
	const selectedSnapshotDate = salesRepAnalysis?.SnapshotDate;

	const aiexperience = useAppSelector(s => s.insights.aiExperience);

	// Sort the sellers by severity
	const sortedSellers = useMemo(() =>
	{
		if (!salesRepAnalysis || salesRepAnalysis.SellerAnalyses == null || salesRepAnalysis.SellerAnalyses.length === 0)
		{
			return [];
		}

		return sortByHighestSeverity(salesRepAnalysis.SellerAnalyses, s => s.Report?.Concerns || [], s => s.SellerName);
	}, [salesRepAnalysis]);


	// Change the troubleshooting tool's snapshot date
	const onChangeSnapshotDate = (date: string) =>
	{
		if (!date)
		{
			return;
		}
		
		changeSnapshotDate(selectedSalesRepId, date);
	};

	const startYear = (new Date().getFullYear()-1).toString();
	const availableDates = salesRepAnalysis?.AvailableDates?.filter(ad => ad > startYear).sort().reverse() || [];

	return (
		<div 
			style={{ 
				display: 'flex', 
				flexDirection: 'column',
				width: '100%', 
				height: '98%', 
				overflow: 'hidden',
			}}>
			<SectionHeader>
				{
					aiexperience === 'MultiSalesRep' && (
						<Button 
							variant='light' 
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								height: 32,
								width: 32,
							}}
							title='Back to Sales Rep List'
							onClick={backToLastView}
						>
							<ArrowBackIcon style={{transform: 'translateY(-1px)'}} />
						</Button>
					)}
				{relationshipTitle} - {salesRepAnalysis?.SalesRepName}
			</SectionHeader>
			<div style={{ 
				display: 'flex', 
				overflowY: 'auto', 
				flexDirection: 'column', 
				padding: '20px 0px',
			}}>
				<StyledDateRowContainer>
					<span style={{ fontWeight: theme.fontWeights.bold, minWidth: 90 }}>Report Date:</span>
					<Select value={selectedSnapshotDate} style={{ width: 150 }} onChange={onChangeSnapshotDate}>
						{
							availableDates.map(date => (
								<Select.Option key={date} value={date}>
									{moment(date, 'YYYY-MM-DD').format('L')}
								</Select.Option>
							))
						}
					</Select>
				</StyledDateRowContainer>
				<div style={{ width: '95%', display: 'flex', flexDirection: 'row', marginTop: 10, marginBottom: 20, gap: 6 }}>
					<div style={{ display: 'flex', flexDirection: 'column', minWidth: 90 }}>
						<div style={{ fontWeight: theme.fontWeights.bold, marginBottom: 5 }}>
							Overview:
						</div>
						<FeedbackThumbs report={salesRepAnalysis?.Report} />
					</div>
					<div style={{ marginLeft: 10 }}>{salesRepAnalysis?.Report?.Overview}</div>
				</div>
				<ReportView report={salesRepAnalysis?.Report} />
				{
					sortedSellers?.length > 0 && (
						<div style={{ marginTop: 20 }}>
							<SectionHeader>
								Sellers
							</SectionHeader>
							<div style={{ display: 'flex',  flexDirection: 'column', alignItems: 'center', paddingBottom: 20 }}>
								{
									sortedSellers?.map((seller) =>
									{
										return (
											<SingleRow
												key={seller.item.SellerUserId}
												Id={seller.item.SellerUserId}
												onSelect={(Id) => onSelectSeller(Id)}
												Name={seller.item.SellerName}
												NumberOfItems={seller.item.GrowerAnalyses?.length}
												NumberOfItemsTitle={'Growers'}
												Severity={seller.highestSeverity}
												SeverityCount={seller.count}
											/>
										);
									})
								}
							</div>
						</div>
					)}
			</div>
		</div>
	);
};
